// Focus (pomodoro) and sleep timers as plain data with wall-clock deadlines (Date.now() ms). Pure: site/js/app.js ticks,
// renders and voices them. A late tick (hidden tab, sleeping laptop) catches up with everything that fell due meanwhile.
export const FOCUS_PRESETS = [
  { id: '25-5', focus: 25, rest: 5 },     // minutes of focus, then minutes of break, repeating
  { id: '50-10', focus: 50, rest: 10 },
];
export const SLEEP_MINUTES = [15, 30, 45, 60, 90];
export const SLEEP_FADE_S = 60;           // the last minute fades out, then the music pauses
const MIN = 60000;

/** a focus session whose first focus period starts at `now` */
export const startFocus = (preset, now) => ({ preset, phase: 'focus', round: 1, endsAt: now + preset.focus * MIN });

/** Moves a focus session on to `now`: focus → break → focus (round + 1) → …, each period starting when the previous one
 *  was due. Returns { focus, ended }: `ended` lists the phases that finished, oldest first (several after a late tick).
 *  A paused session (pauseFocus) stays where it is. */
export function advanceFocus(focus, now) {
  const ended = [];
  if (focus.left != null) return { focus, ended };
  while (now >= focus.endsAt) {
    ended.push(focus.phase);
    focus = focus.phase === 'focus'
      ? { ...focus, phase: 'break', endsAt: focus.endsAt + focus.preset.rest * MIN }
      : { ...focus, phase: 'focus', round: focus.round + 1, endsAt: focus.endsAt + focus.preset.focus * MIN };
  }
  return { focus, ended };
}
/** ends the current period now, so the next one starts now (and runs, if the session was paused) */
export const skipPhase = ({ left, ...focus }, now) => advanceFocus({ ...focus, endsAt: now }, now);
/** Pauses a focus session at `now`: `left` keeps the period's time left, and nothing ends until resumeFocus. */
export const pauseFocus = (focus, now) => (focus.left != null ? focus : { ...focus, left: Math.max(0, focus.endsAt - now) });
/** goes on from a pause at `now`, with the time the period had left */
export function resumeFocus(focus, now) {
  if (focus.left == null) return focus;
  const { left, ...rest } = focus;
  return { ...rest, endsAt: now + left };
}
/** ms left in the current period at `now`, paused or not */
export const focusLeft = (focus, now) => focus.left ?? focus.endsAt - now;
/** focus periods finished so far */
export const roundsDone = focus => focus.round - (focus.phase === 'focus' ? 1 : 0);

export const startSleep = (minutes, now) => ({ minutes, endsAt: now + minutes * MIN, fading: false });
/** What the sleep timer needs at `now`: { action: 'wait' }, { action: 'fade', seconds } (start fading out, to be silent
 *  at the deadline) or { action: 'end' } (time is up). 'fade' comes once: the caller sets `fading` when it acts on it. */
export function sleepStep(sleep, now) {
  const left = (sleep.endsAt - now) / 1000;
  if (left <= 0) return { action: 'end' };
  if (!sleep.fading && left <= SLEEP_FADE_S) return { action: 'fade', seconds: left };
  return { action: 'wait' };
}

/** time left as m:ss (minutes run past 59), rounded up: 25:00 right after the start, 0:01 in the last second */
export function countdown(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
